import React, { useState, useEffect, useRef } from 'react';

const CommandPalette = ({ isOpen, onClose, files, onSelectFile, onExecuteCommand }) => {
  const [query, setQuery] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const inputRef = useRef(null);

  // All available commands
  const commands = [
    { id: 'save', label: 'Save File', icon: '💾', action: 'save', category: 'File' },
    { id: 'format', label: 'Format Document', icon: '✨', action: 'format', category: 'Edit' },
    { id: 'find', label: 'Find in Files', icon: '🔍', action: 'find', category: 'Search' },
    { id: 'replace', label: 'Replace in Files', icon: '🔄', action: 'replace', category: 'Search' },
    { id: 'terminal', label: 'Toggle Terminal', icon: '💻', action: 'terminal', category: 'View' },
    { id: 'ai', label: 'Ask AI', icon: '🤖', action: 'ai', category: 'AI' },
    { id: 'autofix', label: 'Auto-Fix Current File', icon: '🔧', action: 'autofix', category: 'AI' },
    { id: 'build', label: 'Run Build', icon: '🔨', action: 'build', category: 'Build' },
    { id: 'test', label: 'Run Tests', icon: '🧪', action: 'test', category: 'Build' },
    { id: 'git-status', label: 'Git: Status', icon: '📊', action: 'git-status', category: 'Git' },
    { id: 'git-commit', label: 'Git: Commit', icon: '✅', action: 'git-commit', category: 'Git' },
    { id: 'git-push', label: 'Git: Push', icon: '⬆️', action: 'git-push', category: 'Git' },
    { id: 'deploy', label: 'Deploy to Production', icon: '🚀', action: 'deploy', category: 'Deploy' },
    { id: 'snippets', label: 'Insert Snippet', icon: '🧩', action: 'snippets', category: 'Edit' },
    { id: 'new-file', label: 'New File', icon: '📄', action: 'new-file', category: 'File' },
    { id: 'new-project', label: 'New Project', icon: '📁', action: 'new-project', category: 'File' },
    { id: 'settings', label: 'Settings', icon: '⚙️', action: 'settings', category: 'View' },
  ];

  // Filter commands and files based on query
  const getFilteredItems = () => {
    const lowerQuery = query.toLowerCase();

    // If query starts with '>', show only commands
    if (query.startsWith('>')) {
      const commandQuery = query.slice(1).trim().toLowerCase();
      return commands
        .filter(cmd =>
          cmd.label.toLowerCase().includes(commandQuery) ||
          cmd.category.toLowerCase().includes(commandQuery)
        )
        .map(cmd => ({ ...cmd, type: 'command' }));
    }

    // Otherwise, show files first, then commands
    const filteredFiles = files
      .filter(file =>
        file.name.toLowerCase().includes(lowerQuery) ||
        file.path.toLowerCase().includes(lowerQuery)
      )
      .slice(0, 10)
      .map(file => ({ ...file, type: 'file', icon: getFileIcon(file.name) }));

    const filteredCommands = commands
      .filter(cmd => cmd.label.toLowerCase().includes(lowerQuery))
      .slice(0, 5)
      .map(cmd => ({ ...cmd, type: 'command' }));

    return [...filteredFiles, ...filteredCommands];
  };

  const filteredItems = getFilteredItems();

  // Handle keyboard navigation
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e) => {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedIndex(prev => Math.min(prev + 1, filteredItems.length - 1));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedIndex(prev => Math.max(prev - 1, 0));
      } else if (e.key === 'Enter') {
        e.preventDefault();
        if (filteredItems[selectedIndex]) {
          handleSelectItem(filteredItems[selectedIndex]);
        }
      } else if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, selectedIndex, filteredItems]);

  // Focus input when opened
  useEffect(() => {
    if (isOpen) {
      // Use a small timeout to ensure the input is visible before focusing
      const timer = setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.focus();
        }
      }, 50);
      setQuery('');
      setSelectedIndex(0);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  // Reset selected index when query changes
  useEffect(() => {
    setSelectedIndex(0);
  }, [query]);

  const getFileIcon = (filename) => {
    const ext = filename.split('.').pop().toLowerCase();
    const iconMap = {
      js: '📄', jsx: '⚛️', ts: '📘', tsx: '⚛️',
      py: '🐍', html: '🌐', css: '🎨', json: '📋',
      md: '📝', png: '🖼️', jpg: '🖼️', svg: '🎨'
    };
    return iconMap[ext] || '📄';
  };

  const handleSelectItem = (item) => {
    if (item.type === 'file') {
      onSelectFile(item);
    } else if (item.type === 'command') {
      onExecuteCommand(item.action);
    }
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start justify-center pt-20 z-50">
      <div className="bg-gray-900 rounded-lg shadow-2xl w-full max-w-2xl border border-gray-700">
        {/* Input */}
        <div className="p-4 border-b border-gray-700">
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search files or type > for commands..."
            className="w-full bg-gray-800 border border-gray-700 rounded px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          <div className="flex items-center justify-between mt-2 text-xs text-gray-500">
            <span>
              {query.startsWith('>') ? 'Commands' : 'Files & Commands'}
              {filteredItems.length > 0 && ` (${filteredItems.length} results)`}
            </span>
            <div className="flex gap-2">
              <span>↑↓ Navigate</span>
              <span>Enter Select</span>
              <span>Esc Close</span>
            </div>
          </div>
        </div>

        {/* Results */}
        <div className="max-h-96 overflow-y-auto">
          {filteredItems.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              <p className="text-lg mb-2">No results found</p>
              <p className="text-sm">Try typing &gt; to see all commands</p>
            </div>
          ) : (
            <div>
              {filteredItems.map((item, index) => (
                <div
                  key={item.type === 'file' ? item.id : item.id}
                  onClick={() => handleSelectItem(item)}
                  className={`flex items-center gap-3 px-4 py-3 cursor-pointer transition-colors ${
                    index === selectedIndex
                      ? 'bg-blue-600 text-white'
                      : 'text-gray-300 hover:bg-gray-800'
                  }`}
                >
                  <span className="text-2xl">{item.icon}</span>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium truncate">
                      {item.type === 'file' ? item.name : item.label}
                    </div>
                    {item.type === 'file' ? (
                      <div className="text-xs opacity-75 truncate">{item.path}</div>
                    ) : (
                      <div className="text-xs opacity-75">{item.category}</div>
                    )}
                  </div>
                  {item.type === 'command' && (
                    <div className="text-xs opacity-75 px-2 py-1 bg-gray-700 rounded">
                      Command
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-3 border-t border-gray-700 bg-gray-950 rounded-b-lg">
          <div className="flex items-center justify-between text-xs text-gray-500">
            <span>💡 Tip: Use &gt; prefix to search commands only</span>
            <span>Ctrl+P to reopen</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CommandPalette;
